import { useContext } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Auth0Client, CacheKey } from "@auth0/auth0-spa-js"
import { logger } from "utils/logger"
import {
  isAuthProviderUnavailable,
  reportAuthProviderUnavailable
} from "utils/auth-provider-error"
import {
  GET_SSO_CONFIGS_BY_NAME_URL,
  REVOKE_REFRESH_TOKEN
} from "constants/api-url"
import { authApiInstance, userApiInstance } from "utils/axios-utils"
import { buildPath } from "@rentspree/path"
import AuthContext from "../../containers/auth-provider/auth.context"
import { ROUTE } from "../../containers/router/constants"
import {
  AUTH_APP_CLIENT_ID,
  AUTH_PROVIDER_DOMAIN,
  AUTH_API_IDENTIFIER,
  AUTH_APP_SCOPE
} from "../../env"

/**
 * Reference to openid's MFA policy. This is a static value.
 * Specifies the authentication method required in the auth request.
 */
const ACR_MFA_VALUE =
  "http://schemas.openid.net/pape/policies/2007/06/multi-factor"

export const getPartnerOrganizationByName = async name =>
  userApiInstance.get(buildPath(GET_SSO_CONFIGS_BY_NAME_URL, { name }))

export const isPartnerLogin = () => {
  // Auth0 SDK writes the decoded ID token under this suffix and does not export the constant.
  const auth0UserKey = new CacheKey(
    { clientId: AUTH_APP_CLIENT_ID },
    undefined,
    "@@user@@"
  ).toKey()
  const userEntry = localStorage.getItem(auth0UserKey)

  if (!userEntry) {
    throw new Error("Auth0 user cache entry is missing")
  }

  return Boolean(JSON.parse(userEntry).decodedToken?.claims?.org_id)
}

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  const {
    loginWithRedirect,
    logout,
    user,
    loginWithPopup,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims
  } = useAuth0()

  const loginPartnerWithPopup = async ({ provider }) => {
    const organization = await getPartnerOrganizationByName(provider)

    if (!organization) {
      logger.error(`Organization not found. ${provider}`)
      return null
    }

    /*
     To prevent it from changing the current instance state, we must create a new Auth0Client.
     The account that the user desires to link will be managed by this new client.
    */
    const auth0Client = new Auth0Client({
      domain: AUTH_PROVIDER_DOMAIN,
      clientId: AUTH_APP_CLIENT_ID
    })

    await auth0Client.loginWithPopup({
      authorizationParams: {
        max_age: 0,
        scope: "openid",
        rspLoginForLink: true,
        connection: organization.name,
        organization: organization.id
      }
    })

    return auth0Client.getIdTokenClaims()
  }

  const getAccessToken = async () => {
    try {
      return await getAccessTokenSilently()
    } catch (refreshError) {
      if (refreshError?.error === "invalid_grant") {
        logger.info(refreshError)
      } else {
        logger.error(refreshError)
      }
      // Auth0 unreachable during silent refresh: emit the signal the
      // token-refresh outage monitor alerts on. Detection only; behavior unchanged.
      if (isAuthProviderUnavailable(refreshError)) {
        reportAuthProviderUnavailable(refreshError, { source: "token_refresh" })
      }
      window.location.replace(
        `${window.location.origin}/account${ROUTE.SESSION_TIMEOUT}`
      )
      return null
    }
  }

  const revokeRefreshToken = async () => {
    const authTokenKey = new CacheKey({
      clientId: AUTH_APP_CLIENT_ID,
      audience: AUTH_API_IDENTIFIER,
      scope: AUTH_APP_SCOPE
    }).toKey()
    const refreshToken = JSON.parse(localStorage.getItem(authTokenKey))?.body
      ?.refresh_token

    if (refreshToken) {
      try {
        await authApiInstance.post(REVOKE_REFRESH_TOKEN, {
          client_id: AUTH_APP_CLIENT_ID,
          token: refreshToken
        })
      } catch (revokeError) {
        logger.error(revokeError)
      }
    }
  }

  const getAccessTokenLocalStorage = () => {
    const authTokenKey = new CacheKey({
      clientId: AUTH_APP_CLIENT_ID,
      audience: AUTH_API_IDENTIFIER,
      scope: AUTH_APP_SCOPE
    }).toKey()
    const accessToken = JSON.parse(localStorage.getItem(authTokenKey))?.body
      ?.access_token
    return accessToken
  }

  const isStepUpAuthenticated = async () => {
    try {
      const idToken = await getIdTokenClaims()
      return idToken?.amr?.includes("mfa") ?? false
    } catch {
      return false
    }
  }

  const stepUpAuthentication = async () => {
    if (await isStepUpAuthenticated()) {
      return true
    }
    await loginWithPopup(
      { authorizationParams: { acr_values: ACR_MFA_VALUE } },
      { timeoutInSeconds: 300 }
    )
    return isStepUpAuthenticated()
  }

  return {
    loginWithRedirect,
    logout,
    user,
    loginWithPopup,
    isAuthenticated,
    isLoading,
    loginPartnerWithPopup,
    getAccessToken,
    getIdTokenClaims,
    revokeRefreshToken,
    getPartnerOrganizationByName,
    partnerOrganization: context.partnerOrganization,
    getAccessTokenLocalStorage,
    stepUpAuthentication
  }
}
